<!-- CityExport.vue - "Export & API": choose what to download first, nothing is exported by opening the page -->
<template>
  <div class="page-col">
    <header class="city-head"><div class="titles"><h1>{{ $t('city.exportPage.title') }}</h1><p class="muted">{{ $t('city.exportPage.intro') }}</p></div></header>

    <section class="card" aria-labelledby="exp-h">
      <h2 id="exp-h">{{ $t('city.exportPage.optionsTitle') }}</h2>

      <fieldset class="group">
        <legend>{{ $t('city.exportPage.statusLegend') }}</legend>
        <label v-for="o in statusOptions" :key="o.value" class="choice">
          <input v-model="status" type="radio" name="exp-status" :value="o.value" />
          <span>{{ o.label }}</span>
        </label>
      </fieldset>

      <fieldset class="group">
        <legend>{{ $t('city.exportPage.formatLegend') }}</legend>
        <label class="choice">
          <input v-model="format" type="radio" name="exp-format" value="csv" aria-describedby="fmt-csv" />
          <span>{{ $t('city.exportPage.formatCsv') }}<small id="fmt-csv">{{ $t('city.exportPage.formatCsvHint') }}</small></span>
        </label>
        <label class="choice">
          <input v-model="format" type="radio" name="exp-format" value="json" aria-describedby="fmt-json" />
          <span>{{ $t('city.exportPage.formatJson') }}<small id="fmt-json">{{ $t('city.exportPage.formatJsonHint') }}</small></span>
        </label>
      </fieldset>

      <p class="count" role="status">
        <template v-if="count === null && !countFailed">{{ $t('city.exportPage.counting') }}</template>
        <template v-else-if="countFailed">{{ $t('city.exportPage.countError') }}</template>
        <template v-else>{{ $t('city.exportPage.count', count) }}</template>
      </p>
      <p class="muted">{{ $t('city.exportPage.contents') }}</p>

      <div class="row">
        <button type="button" class="btn-standard" :disabled="busy" @click="download">
          {{ busy ? $t('city.exportPage.downloading') : $t('city.exportPage.download') }}
        </button>
      </div>
      <p v-if="done" class="ok" role="status"><i class="fas fa-check" aria-hidden="true"></i> {{ $t('city.exportPage.done') }}</p>
      <p v-if="failed" class="err" role="alert"><i class="fas fa-triangle-exclamation" aria-hidden="true"></i> {{ $t('city.exportPage.error') }}</p>
    </section>

    <section class="card" aria-labelledby="api-h">
      <h2 id="api-h">{{ $t('city.exportPage.apiTitle') }}</h2>
      <p class="muted">{{ $t('city.exportPage.apiIntro') }}</p>
      <dl class="facts"><div><dt>{{ $t('city.exportPage.apiBase') }}</dt><dd class="mono">{{ base }}</dd></div></dl>
      <div class="table-wrap">
        <table>
          <thead><tr><th scope="col">{{ $t('city.exportPage.apiEndpoint') }}</th><th scope="col">{{ $t('city.exportPage.apiPurpose') }}</th></tr></thead>
          <tbody>
            <tr v-for="e in endpoints" :key="e.key"><td class="mono">{{ e.path }}</td><td>{{ $t('city.exportPage.apiRows.' + e.key) }}</td></tr>
          </tbody>
        </table>
      </div>
      <p class="muted">{{ $t('city.exportPage.apiExample') }}</p>
      <pre class="code" tabindex="0"><code>curl -H "Authorization: Bearer &lt;token&gt;" \
  "{{ base }}/municipal/export.csv?status=fixed&amp;format=json"</code></pre>
    </section>
  </div>
</template>

<script>
import { muniFetch, STATUSES } from '@/utils/municipal';

export default {
  name: 'CityExport',
  props: { muni: { type: Object, required: true } },
  data() {
    return {
      status: '', format: 'csv', count: null, countFailed: false, busy: false, done: false, failed: false,
      base: process.env.VUE_APP_SERVER_URL || '',
      endpoints: [
        { key: 'me', path: 'GET /municipal/me' },
        { key: 'overview', path: 'GET /municipal/overview' },
        { key: 'reports', path: 'GET /municipal/reports?status=' },
        { key: 'export', path: 'GET /municipal/export.csv?status=&format=' },
      ],
    };
  },
  computed: {
    statusOptions() {
      return [{ value: '', label: this.$t('city.exportPage.statusAll') }, ...STATUSES.map(s => ({ value: s, label: this.$t('city.status.' + s) }))];
    },
  },
  watch: {
    status: { immediate: true, handler() { this.done = false; this.failed = false; this.loadCount(); } },
    format() { this.done = false; this.failed = false; },
  },
  methods: {
    // How many reports the export will contain, so nobody downloads something unexpected
    async loadCount() {
      this.count = null; this.countFailed = false;
      const wanted = this.status;
      try {
        const data = await (await muniFetch(this.$pb, '/municipal/reports?page=1' + (wanted ? '&status=' + wanted : ''))).json();
        if (wanted === this.status) this.count = data.total;
      } catch (e) { if (wanted === this.status) this.countFailed = true; }
    },
    async download() {
      this.busy = true; this.done = false; this.failed = false;
      try {
        const q = new URLSearchParams({ format: this.format });
        if (this.status) q.set('status', this.status);
        const res = await muniFetch(this.$pb, '/municipal/export.csv?' + q.toString());
        const url = URL.createObjectURL(await res.blob());
        const a = document.createElement('a');
        a.href = url; a.download = `${this.muni.slug}-reports${this.status ? '-' + this.status : ''}.${this.format}`; a.click();
        URL.revokeObjectURL(url);
        this.done = true;
      } catch (e) { this.failed = true; }
      this.busy = false;
    },
  },
};
</script>

<style scoped>
.page-col { display: flex; flex-direction: column; gap: 24px; max-width: 720px; }
.group { border: 0; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.group legend { font-weight: 600; padding: 0; margin-bottom: 4px; }
.choice { display: flex; align-items: flex-start; gap: 10px; min-height: var(--touch-min); padding: 8px 4px; cursor: pointer; }
.choice input { width: 20px; height: 20px; margin: 2px 0 0; flex: none; accent-color: var(--color-status-done); }
.choice input:focus-visible { outline: 3px solid var(--color-primary-text); outline-offset: 2px; }
.choice span { display: flex; flex-direction: column; gap: 2px; }
.choice small { font-size: 13px; color: var(--color-secondary); }
.count { margin: 0; font-weight: 600; }
.row { display: flex; gap: 12px; flex-wrap: wrap; }
.ok, .err { margin: 0; display: flex; gap: 8px; align-items: center; font-weight: 600; }
.ok { color: var(--color-status-done); }
.err { color: var(--color-error-text); }
.facts { margin: 0; } .facts dt { font-size: 13px; color: var(--color-secondary); } .facts dd { margin: 0; font-weight: 600; }
.mono { font-family: var(--font-mono); font-size: 13px; overflow-wrap: anywhere; }
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { text-align: start; padding: 8px 10px; border-bottom: 1px solid var(--color-border); vertical-align: top; }
th { font-size: 13px; color: var(--color-secondary); }
.code { margin: 0; padding: 12px; overflow-x: auto; background: var(--color-subtle); border-radius: var(--radius-md); font-family: var(--font-mono); font-size: 13px; }
.code:focus-visible { outline: 3px solid var(--color-primary-text); outline-offset: 2px; }
@media (forced-colors: active) { .choice input { accent-color: auto; } }
</style>
